import type { TFunction } from '@unifyapps/i18n/client';
import _identity from 'lodash/identity';
import type React from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { getStyleItemConfig } from '../../hooks/useBlockAppearanceStyles/styles/utils';
import type { AppearanceStyles, StyleResult } from '../../hooks/useBlockAppearanceStyles/types';
import { getBlockAppearanceAndStyles } from '../../hooks/useBlockAppearanceStyles/utils';
import { useInterfaceApplicationType } from '../../stores/app/GlobalAppStateStore';

type ProcessStylesFn = (appearanceStyles: AppearanceStyles) => StyleResult;

export type BlockAppearanceStylesContextType = {
  processStyles: ProcessStylesFn;
};

const BlockAppearanceStylesContext = createContext<BlockAppearanceStylesContextType | null>(null);

export function BlockAppearanceStylesProvider({ children }: { children: React.ReactNode }) {
  const applicationType = useInterfaceApplicationType();

  const styleItemsConfig = useMemo(
    /**
     * Labels should not be needed for processing styles and converting them to
     * classNames and styles. Hence passing identity.
     */
    () => getStyleItemConfig(_identity as TFunction, applicationType),
    [applicationType],
  );

  const processStyles = useCallback(
    (appearanceStyles: AppearanceStyles): StyleResult =>
      getBlockAppearanceAndStyles({
        appearanceStyles,
        styleItemsConfig,
      }),
    [styleItemsConfig],
  );

  const contextValue = useMemo(() => ({ processStyles }), [processStyles]);

  return (
    <BlockAppearanceStylesContext.Provider value={contextValue}>
      {children}
    </BlockAppearanceStylesContext.Provider>
  );
}

export const useBlockAppearanceStylesContext = (): BlockAppearanceStylesContextType => {
  const context = useContext(BlockAppearanceStylesContext);

  if (!context) {
    throw new Error(
      'useBlockAppearanceStylesContext must be used within a BlockAppearanceStylesProvider',
    );
  }

  return context;
};
